import React, { useRef, useEffect, useState, useCallback } from 'react';

/**
 * CLI-styled multiline input that looks and feels like a Cisco IOS terminal.
 * Green-on-black, monospace, captures Tab, supports command history.
 */
export default function TerminalInput({
  prompt = 'Router#',
  onSubmit,
  disabled = false,
  autoFocus = true,
  multiline = true,
}) {
  const textareaRef = useRef(null);
  const [value, setValue] = useState('');
  const [history, setHistory] = useState([]);
  const [historyIdx, setHistoryIdx] = useState(-1);

  // Auto-focus on mount and when re-enabled
  useEffect(() => {
    if (autoFocus && !disabled && textareaRef.current) {
      textareaRef.current.focus();
    }
  }, [autoFocus, disabled]);

  const handleKeyDown = useCallback((e) => {
    if (disabled) return;

    // Tab capture — don't leave the input
    if (e.key === 'Tab') {
      e.preventDefault();
      // Insert 2 spaces (like IOS tab completion would add a space)
      const start = e.target.selectionStart;
      const newValue = value.slice(0, start) + '  ' + value.slice(e.target.selectionEnd);
      setValue(newValue);
      // Set cursor position after inserted spaces
      setTimeout(() => {
        e.target.selectionStart = e.target.selectionEnd = start + 2;
      }, 0);
      return;
    }

    // Submit on Enter (Ctrl+Enter for multiline, just Enter for single-line)
    if (e.key === 'Enter') {
      if (multiline && !e.ctrlKey && !e.metaKey) {
        return; // Allow newline in multiline mode
      }
      if (!multiline || e.ctrlKey || e.metaKey) {
        e.preventDefault();
        handleSubmit();
        return;
      }
    }

    // Up arrow — command history
    if (e.key === 'ArrowUp' && history.length > 0) {
      e.preventDefault();
      const newIdx = historyIdx < history.length - 1 ? historyIdx + 1 : historyIdx;
      setHistoryIdx(newIdx);
      setValue(history[history.length - 1 - newIdx] || '');
    }

    // Down arrow — forward in history
    if (e.key === 'ArrowDown' && historyIdx >= 0) {
      e.preventDefault();
      const newIdx = historyIdx - 1;
      setHistoryIdx(newIdx);
      setValue(newIdx >= 0 ? history[history.length - 1 - newIdx] : '');
    }
  }, [value, disabled, multiline, history, historyIdx]);

  const handleSubmit = useCallback(() => {
    const trimmed = value.trim();
    if (!trimmed) return;
    setHistory(prev => [...prev, trimmed]);
    setHistoryIdx(-1);
    onSubmit?.(trimmed);
    setValue('');
  }, [value, onSubmit]);

  return (
    <div className="bg-black rounded-lg border border-gray-700 overflow-hidden">
      {/* Terminal header bar */}
      <div className="flex items-center gap-2 px-3 py-1.5 bg-gray-900 border-b border-gray-700">
        <div className="w-3 h-3 rounded-full bg-red-500" />
        <div className="w-3 h-3 rounded-full bg-yellow-500" />
        <div className="w-3 h-3 rounded-full bg-green-500" />
        <span className="text-xs text-gray-500 ml-2 font-mono">IOS-XE Terminal</span>
      </div>

      {/* Input area */}
      <div className="p-3">
        <div className="flex items-start gap-0">
          <span className="text-green-400 font-mono text-sm select-none shrink-0 pt-0.5">
            {prompt}{' '}
          </span>
          <textarea
            ref={textareaRef}
            value={value}
            onChange={(e) => { setValue(e.target.value); setHistoryIdx(-1); }}
            onKeyDown={handleKeyDown}
            disabled={disabled}
            rows={multiline ? 4 : 1}
            spellCheck={false}
            autoComplete="off"
            autoCorrect="off"
            autoCapitalize="off"
            className="flex-1 bg-transparent text-green-400 font-mono text-sm outline-none resize-none placeholder-green-900 caret-green-400"
            placeholder={disabled ? '' : multiline ? 'Type commands... (Ctrl+Enter to submit)' : 'Type command... (Enter to submit)'}
            style={{
              minHeight: multiline ? '96px' : '24px',
            }}
          />
        </div>
      </div>

      {/* Submit bar */}
      {!disabled && (
        <div className="flex items-center justify-between px-3 py-2 bg-gray-900/50 border-t border-gray-800">
          <span className="text-xs text-gray-600 font-mono">
            {multiline ? 'Ctrl+Enter to submit' : 'Enter to submit'}
          </span>
          <button
            onClick={handleSubmit}
            disabled={!value.trim()}
            className="px-3 py-1 bg-green-700 text-green-100 text-xs rounded hover:bg-green-600 disabled:opacity-30 disabled:cursor-not-allowed font-mono"
          >
            Submit
          </button>
        </div>
      )}
    </div>
  );
}
